<script lang="ts" setup>
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { RegisterSettingStore } from '../../RegisterSettingStore';
import { storeToRefs } from 'pinia';
import SelectableField from '../SelectableField.vue';
import { computed, ref, watch } from 'vue';
const t = i18n.t;

const props = defineProps<{
  store: RegisterSettingStore;
}>();

const { state } = storeToRefs(props.store);
const selectedFieldId = ref<string>('');
const emits = defineEmits<{
  (e: 'on-click-back'): void;
}>();

const imagesRequire = require.context('@/assets/img', false, /^\.\/register-input-step-flow-3.*\.png$/);
const localedRegisterInputStepFlowImage = ref(imagesRequire('./register-input-step-flow-3.png'));

watch(
  () => i18n.locale,
  (newVal) => {
    localedRegisterInputStepFlowImage.value =
      newVal === 'en' ? imagesRequire('./register-input-step-flow-3__en.png') : imagesRequire('./register-input-step-flow-3.png');
  },
  {
    immediate: true,
  }
);

/**
 * 戻るボタン
 */
const onClickBackButton = () => {
  emits('on-click-back');
};

const selectedFieldSuggestions = computed(() => {
  return state.value.uploadBusiness.businessHeaderCandidate.map((candidate) => candidate.value);
});
</script>

<template>
  <div class="finish-business">
    <div class="finish-business__title">{{ t('取引紐付け：タイトル') }}</div>

    <img :src="localedRegisterInputStepFlowImage" class="finish-business__progress" />

    <div class="finish-business__sub-title sub-title">
      <span class="sub-title__prefix"> {{ t('取引紐付け：本文') }}</span>
      <span class="sub-title__suffix"> {{ t('取引紐付け：注釈') }}</span>
    </div>

    <div class="finish-business__radio-button-container radio-button-container">
      <div class="radio-button-container__default-input">
        <input
          id="radio-button-container__button--default-input"
          v-model="state.linkingBusiness.useLinkingType"
          class="radio-button-container__button"
          type="radio"
          value="Default"
        />
        <label class="radio-button-container__label" for="radio-button-container__button--default-input">{{
          t('取引紐付け：ラジオボタン：デフォルト書式')
        }}</label>
      </div>

      <div class="radio-button-container__original-input">
        <input
          id="radio-button-container__button--original-input"
          v-model="state.linkingBusiness.useLinkingType"
          class="radio-button-container__button"
          type="radio"
          value="Original"
        />
        <label class="radio-button-container__label" for="radio-button-container__button--original-input">{{
          t('取引紐付け：ラジオボタン：オリジナル書式')
        }}</label>
      </div>
    </div>

    <div class="finish-business__linking-container linking-container">
      <template v-for="header in state.linkingBusiness.linkedBusinessHeaders" :key="`finish-business-header-${header.key}`">
        <div class="linking-container__at">
          <div class="linking-container__at--content">
            <span class="linking-container__at--label">{{ t(header.displayName) + (header.required ? '*' : '') }}</span>
            <SelectableField
              :value="header.value"
              :selected-id="selectedFieldId"
              :hint="t('取引紐付け：サジェストヒント')"
              :suggestions="selectedFieldSuggestions"
            />
          </div>
          <span class="linking-container__at--error">{{
            header.validateError == 'NotSelected' ? t('取引紐付け：アラート文言：必須項目') : '　'
          }}</span>
        </div>
      </template>
    </div>

    <div class="finish-business__button-container">
      <TextButton
        :button-style="'btn-secondary'"
        :size="'btn-md'"
        :on-click="onClickBackButton"
        :disabled="false"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：戻るボタン') }}
      </TextButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.finish-business {
  display: grid;
  gap: 12px;

  background-color: $semanticWhite;
  border: 1px solid $borderDefault;
  border-radius: 4px;
  margin-top: 16px;
  padding: 16px;

  &__title {
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;
  }

  &__progress {
    justify-self: center;
  }

  &__button-container {
    display: grid;
    justify-self: center;
    gap: 8px;
    grid-template-columns: repeat(2, fit-content(100%));
    margin-top: 16px;
  }
}

.sub-title {
  display: grid;
  gap: 16px;
  grid-template-rows: repeat(2, fit-content(100%));

  &__prefix {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;
  }

  &__suffix {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $textDefault;
  }
}

.radio-button-container {
  display: grid;
  grid-template-rows: repeat(2, fit-content(100%));
  gap: 16px;
  pointer-events: none;

  &__default-input {
    display: grid;
    grid-template-columns: repeat(2, fit-content(100%));
    align-items: end;
  }

  &__original-input {
    display: grid;
    grid-template-columns: repeat(2, fit-content(100%));
    align-items: end;
  }

  &__button {
    width: 18px;
    height: 18px;
  }

  &__label {
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    font-weight: 400;
    color: $textDefault;
    padding: 0px 8px;
  }
}

.linking-container {
  display: grid;
  margin-top: 16px;
  gap: 32px;
  grid-template-columns: repeat(auto-fit, minmax(100px, 300px));
  pointer-events: none;

  &__at {
    display: grid;
    grid-template-rows: repeat(2, fit-content(100%));

    &--content {
      display: grid;
      grid-template-rows: repeat(2, fit-content(100%));
      border-bottom: 1px solid $borderDefault;
    }

    &--label {
      margin-bottom: 8px;
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $textDefault;
    }

    &--error {
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $semanticError;
    }
  }
}
</style>
